import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Langue, SourceDemande } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Demandes — `/v1/g/:slug/demandes` (`docs/dev/api.md` § 14.4). Les formes
 * suivent `apps/api/src/demandes/commun.ts` et `demandes.routes.ts` au champ
 * près.
 */

export type EtatDemande = 'a_traiter' | 'repondue' | 'close';
export type { SourceDemande } from '@artosera/shared';

export interface DemandePresentee {
  readonly id: string;
  readonly oeuvreId: string | null;
  readonly expositionId: string | null;
  readonly contactId: string | null;
  readonly nom: string;
  readonly email: string;
  readonly telephone: string;
  readonly langue: Langue;
  readonly sujet: string;
  readonly message: string;
  readonly source: SourceDemande;
  readonly etat: EtatDemande;
  readonly assigneeA: string | null;
  /** Nom de la personne assignée (membre de la galerie), rendu par l'API. */
  readonly assigneeNom?: string | null;
  readonly note: string;
  readonly recueLe: string;
  readonly repondueLe: string | null;
  readonly creeeLe: string;
  readonly modifieeLe: string;
}

export interface CorpsDemande {
  nom: string;
  email: string;
  telephone?: string;
  langue?: Langue;
  sujet?: string;
  message: string;
  oeuvreId?: string | null;
  contactId?: string | null;
  source?: SourceDemande;
  assigneeA?: string | null;
  note?: string;
  rattacherAuCarnet?: boolean;
}

export interface CorpsMiseAJourDemande {
  etat?: EtatDemande;
  assigneeA?: string | null;
  note?: string;
}

export interface FiltresDemandes {
  q?: string;
  etat?: readonly EtatDemande[];
  assigneeA?: string;
  assignation?: 'aucune' | 'toute';
  oeuvreId?: string;
  contactId?: string;
  source?: SourceDemande;
  tri?: 'recent' | 'ancien';
  page?: number;
  parPage?: number;
}

export interface ReponseListeDemandes {
  readonly demandes: readonly DemandePresentee[];
  readonly aTraiter: number;
  readonly pagination: Pagination;
}

export interface FicheDemande {
  readonly demande: DemandePresentee;
  readonly contact: { readonly id: string; readonly nom: string } | null;
  readonly oeuvre: {
    readonly id: string;
    readonly titre: string;
    readonly slug: string;
    readonly statut: string;
  } | null;
}

export interface ReponseRattachement {
  readonly demande: DemandePresentee;
  readonly contactId: string;
  readonly contactCree: boolean;
}

function requeteFiltres(filtres: FiltresDemandes): string {
  const params = new URLSearchParams();
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.etat && filtres.etat.length > 0) params.set('etat', filtres.etat.join(','));
  if (filtres.assigneeA) params.set('assigneeA', filtres.assigneeA);
  if (filtres.assignation) params.set('assignation', filtres.assignation);
  if (filtres.oeuvreId) params.set('oeuvreId', filtres.oeuvreId);
  if (filtres.contactId) params.set('contactId', filtres.contactId);
  if (filtres.source) params.set('source', filtres.source);
  params.set('tri', filtres.tri ?? 'recent');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 50));
  return params.toString();
}

export const demandesApi = {
  lister: (galerie: string, filtres: FiltresDemandes = {}, appel?: AppelApi) =>
    api.get<ReponseListeDemandes>(
      `/g/${encodeURIComponent(galerie)}/demandes?${requeteFiltres(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheDemande>(`/g/${encodeURIComponent(galerie)}/demandes/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsDemande, appel?: AppelApi) =>
    api.post<{ demande: DemandePresentee; contactCree: boolean }>(
      `/g/${encodeURIComponent(galerie)}/demandes`,
      corps,
      { galerie, ...appel },
    ),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourDemande, appel?: AppelApi) =>
    api.patch<{ demande: DemandePresentee }>(
      `/g/${encodeURIComponent(galerie)}/demandes/${id}`,
      corps,
      { galerie, ...appel },
    ),
  rattacher: (galerie: string, id: string, contactId?: string, appel?: AppelApi) =>
    api.post<ReponseRattachement>(
      `/g/${encodeURIComponent(galerie)}/demandes/${id}/rattacher`,
      contactId ? { contactId } : {},
      { galerie, ...appel },
    ),
};

const cleListe = (galerie: string, filtres: FiltresDemandes) =>
  cleMetier(galerie, 'demandes', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'demandes', 'fiche', id);

export function useDemandes(galerie: string, filtres: FiltresDemandes = {}) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => demandesApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}

export function useDemande(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => demandesApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

function invaliderDemandes(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'demandes', 'liste') });
}

export function useCreerDemande(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsDemande, appel) => demandesApi.creer(galerie, corps, appel),
      onSuccess: () => {
        invaliderDemandes(client, galerie);
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'contacts', 'liste') });
      },
    }),
  );
}

export function useModifierDemande(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourDemande }, appel) =>
        demandesApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderDemandes(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useRattacherDemande(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, contactId }: { id: string; contactId?: string }, appel) =>
        demandesApi.rattacher(galerie, id, contactId, appel),
      onSuccess: (_donnees, variables) => {
        invaliderDemandes(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'contacts', 'liste') });
      },
    }),
  );
}
